import * as React from 'react';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Popover from 'antd/lib/popover';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Text from 'antd/lib/typography/Text';
import Flex from 'antd/lib/flex';
import { EditOutlined, RocketOutlined, StopOutlined } from '@ant-design/icons';
import { DestinationSummary, ReleaseStatus } from '../../types';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { antGrey, colorWithAlpha, rwColors } from '../styles/globalStyles';
import { Destination } from '@/types/destinations';
import { displayStringForDestination } from '@/helpers/displayHelpers';

const MAX_VISIBLE_DESTINATIONS = 3;

interface DestinationsBannerProps {
    destinationSummaries: DestinationSummary[];
    showEditButton?: boolean;
    onEditClick?: () => void;
}

interface DestinationRowProps {
    icon: React.ReactNode;
    label: string;
    destinations: DestinationSummary[];
    opacity?: number;
    labelColor?: string;
}

const destinationSummaryToDestination = (summary: DestinationSummary): Destination => ({
    stepConfigId: summary.stepConfigId,
    name: summary.displayName,
    platform: summary.platform,
    appStoreType: summary.appStoreType,
    iconURL: summary.iconURL,
});

const DestinationItem: React.FC<{ destination: DestinationSummary }> = ({ destination }) => {
    const dest = destinationSummaryToDestination(destination);
    return (
        <Space align="center" size={8}>
            <DestinationCounter destinations={[dest]} size={16} />
            <Text style={{ fontWeight: 400, fontSize: 14, color: antGrey.grey8 }}>
                {displayStringForDestination(dest.platform, dest.appStoreType, dest.name, dest.appName)}
            </Text>
        </Space>
    );
};

const DestinationRow: React.FC<DestinationRowProps> = ({
    icon,
    label,
    destinations,
    opacity = 1,
    labelColor = antGrey.grey8,
}) => {
    if (destinations.length === 0) {
        return null;
    }

    const visibleDestinations = destinations.slice(0, MAX_VISIBLE_DESTINATIONS);
    const remainingDestinations = destinations.slice(MAX_VISIBLE_DESTINATIONS);
    const hasMore = remainingDestinations.length > 0;

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, opacity }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 100 }}>
                {icon}
                <Text style={{ fontWeight: 600, fontSize: 14, color: labelColor }}>{label}</Text>
            </span>
            <Space split={<Divider type="vertical" />} align="center" size={14}>
                {visibleDestinations.map((dest) => (
                    <DestinationItem key={dest.id} destination={dest} />
                ))}
                {hasMore && (
                    <Popover
                        content={
                            <div style={{ maxWidth: 300, maxHeight: 150, overflowY: 'auto', padding: '8px 4px' }}>
                                {destinations.map((dest) => (
                                    <div key={dest.id} style={{ padding: '8px 10px' }}>
                                        <DestinationItem destination={dest} />
                                    </div>
                                ))}
                            </div>
                        }
                        trigger="hover"
                        placement="bottom"
                    >
                        <Text style={{ color: '#1890ff', cursor: 'pointer' }}>
                            + {remainingDestinations.length} more
                        </Text>
                    </Popover>
                )}
            </Space>
        </div>
    );
};

const DestinationsBanner: React.FC<DestinationsBannerProps> = ({
    destinationSummaries,
    showEditButton,
    onEditClick,
}) => {
    const releasingDestinations = destinationSummaries.filter(
        (dest) => dest.destinationStatus === ReleaseStatus.Active,
    );
    const excludedDestinations = destinationSummaries.filter(
        (dest) => dest.destinationStatus === ReleaseStatus.Skipped,
    );
    const allDestinationsIncluded = excludedDestinations.length === 0;

    if (!showEditButton && excludedDestinations.length === 0) {
        return null;
    }

    return (
        <Alert
            type="info"
            showIcon={false}
            message={
                <Flex justify="space-between" align="flex-start" style={{ padding: '8px 12px' }}>
                    <div>
                        <Text style={{ fontWeight: 600, fontSize: 16, color: antGrey.grey8 }}>
                            {allDestinationsIncluded ?
                                'This release includes all destinations.'
                            :   'This release does not include all destinations.'}
                        </Text>
                        <DestinationRow
                            icon={<RocketOutlined style={{ color: antGrey.grey8 }} />}
                            label="Releasing"
                            destinations={releasingDestinations}
                            labelColor={antGrey.grey8}
                        />
                        <DestinationRow
                            icon={<StopOutlined style={{ color: antGrey.grey9 }} />}
                            label="Excluded"
                            destinations={excludedDestinations}
                            opacity={0.5}
                            labelColor={antGrey.grey9}
                        />
                    </div>
                    {showEditButton && (
                        <Button type="link" icon={<EditOutlined />} onClick={onEditClick}>
                            Edit destinations
                        </Button>
                    )}
                </Flex>
            }
            style={{
                marginBottom: 16,
                borderColor: colorWithAlpha(rwColors.primary, 0.5),
                borderWidth: 1,
                borderStyle: 'solid',
            }}
        />
    );
};

export default DestinationsBanner;
